import type { NavItem, NavItemType, SiteNavSettings } from "./types";

export const defaultFooterServiceSlugs = [
  "audit-assurance",
  "tax-services",
  "advisory",
  "esg-advisory",
  "business-outsourcing-services",
] as const;

export const defaultHeaderMenu: NavItem[] = [
  { id: "nav-services", label: "Services", type: "mega_services", visible: true },
  { id: "nav-insights", label: "Insights", type: "mega_insights", visible: true },
  { id: "nav-stories", label: "Client Stories", type: "mega_stories", visible: true },
  { id: "nav-careers", label: "Careers", type: "link", href: "/careers", visible: true },
  { id: "nav-about", label: "About Us", type: "link", href: "/about", visible: true },
];

export const defaultFooterMenu: NavItem[] = [
  {
    id: "footer-insights",
    label: "Insights",
    type: "link",
    visible: true,
    children: [
      { id: "footer-insights-all", label: "All Insights", type: "link", href: "/insights", visible: true },
      { id: "footer-stories", label: "Client Stories", type: "link", href: "/client-stories", visible: true },
      { id: "footer-industries", label: "Industries", type: "link", href: "/industries", visible: true },
    ],
  },
  {
    id: "footer-company",
    label: "Company",
    type: "link",
    visible: true,
    children: [
      { id: "footer-about", label: "About Us", type: "link", href: "/about", visible: true },
      { id: "footer-careers", label: "Careers", type: "link", href: "/careers", visible: true },
      { id: "footer-contact", label: "Contact", type: "link", href: "/contact", visible: true },
    ],
  },
  {
    id: "footer-legal",
    label: "Legal",
    type: "link",
    visible: true,
    children: [
      { id: "footer-privacy", label: "Privacy Policy", type: "link", href: "/privacy", visible: true },
      { id: "footer-terms", label: "Terms of Service", type: "link", href: "/terms", visible: true },
    ],
  },
];

const NAV_ITEM_TYPES: NavItemType[] = ["link", "mega_services", "mega_insights", "mega_stories"];

function parseNavItem(raw: unknown): NavItem | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.label !== "string") return null;
  const type = NAV_ITEM_TYPES.includes(v.type as NavItemType) ? (v.type as NavItemType) : "link";
  const children = Array.isArray(v.children)
    ? v.children.map(parseNavItem).filter((c): c is NavItem => c !== null)
    : undefined;
  return {
    id: v.id,
    label: v.label,
    href: typeof v.href === "string" ? v.href : undefined,
    type,
    children: children?.length ? children : undefined,
    visible: typeof v.visible === "boolean" ? v.visible : true,
  };
}

export function parseNavMenu(raw: unknown, fallback: NavItem[]): NavItem[] {
  if (!Array.isArray(raw)) return fallback;
  const parsed = raw.map(parseNavItem).filter((item): item is NavItem => item !== null);
  return parsed.length > 0 ? parsed : fallback;
}

export function parseFooterServiceSlugs(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [...defaultFooterServiceSlugs];
  const slugs = raw.filter((s): s is string => typeof s === "string" && s.length > 0);
  return slugs.length > 0 ? slugs : [...defaultFooterServiceSlugs];
}

export function parseSiteNavSettings(raw: {
  header_menu?: unknown;
  footer_menu?: unknown;
  footer_service_slugs?: unknown;
}): SiteNavSettings {
  return {
    headerMenu: parseNavMenu(raw.header_menu, defaultHeaderMenu),
    footerMenu: parseNavMenu(raw.footer_menu, defaultFooterMenu),
    footerServiceSlugs: parseFooterServiceSlugs(raw.footer_service_slugs),
  };
}

export const defaultSiteNavSettings: SiteNavSettings = {
  headerMenu: defaultHeaderMenu,
  footerMenu: defaultFooterMenu,
  footerServiceSlugs: [...defaultFooterServiceSlugs],
};
